import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { anlagenAPI, qrCodeAPI, kundenAPI } from '../services/api';
import { ArrowLeft, Save, QrCode as QrCodeIcon } from 'lucide-react';
import Navbar from '../components/Navbar';

const AnlageForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    customer_id: new URLSearchParams(location.search).get('kunde') || '',
    anlagentyp: 'Kälteanlage',
    hersteller: '',
    modell: '',
    seriennummer: '',
    standort_adresse: '',
    standort_gebaeude: '',
    standort_raum: '',
    leistung: '',
    baujahr: '',
    kaeltemittel: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [kunden, setKunden] = useState([]);

  useEffect(() => {
    loadKunden();
    if (isEdit) {
      loadAnlage();
    }
  }, [id]);

  const loadKunden = async () => {
    try {
      const data = await kundenAPI.getAll();
      setKunden(data);
    } catch (error) {
      console.error('Fehler beim Laden der Kunden:', error);
    }
  };

  const loadAnlage = async () => {
    try {
      const data = await anlagenAPI.getById(id);
      setFormData({
        customer_id: data.customer_id || '',
        anlagentyp: data.anlagentyp,
        hersteller: data.hersteller,
        modell: data.modell,
        seriennummer: data.seriennummer,
        standort_adresse: data.standort_adresse,
        standort_gebaeude: data.standort_gebaeude,
        standort_raum: data.standort_raum,
        leistung: data.leistung,
        baujahr: data.baujahr,
        kaeltemittel: data.kaeltemittel || ''
      });
      setQrCode(data.qr_code);
    } catch (error) {
      console.error('Fehler beim Laden der Anlage:', error);
      setError('Anlage konnte nicht geladen werden');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEdit) {
        await anlagenAPI.update(id, formData);
      } else {
        const newAnlage = await anlagenAPI.create(formData);
        setQrCode(newAnlage.qr_code);
      }
      
      if (isEdit) {
        navigate(`/anlage/${id}`);
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setError(error.response?.data?.detail || 'Fehler beim Speichern der Anlage');
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = () => {
    const link = document.createElement('a');
    link.href = qrCodeAPI.generate(qrCode);
    link.download = `qr-code-${formData.seriennummer || 'anlage'}.png`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </button>

        <div className="bg-white rounded-xl shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8" data-testid="form-title">
            {isEdit ? 'Anlage bearbeiten' : 'Neue Anlage erstellen'}
          </h1>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {qrCode && !isEdit && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-green-900 mb-2">✅ Anlage erfolgreich erstellt!</h3>
                  <p className="text-green-700">QR-Code wurde generiert. Sie können ihn jetzt herunterladen und ausdrucken.</p>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <img 
                    src={qrCodeAPI.generate(qrCode)} 
                    alt="QR Code"
                    className="w-32 h-32 border-4 border-white rounded-lg shadow-lg"
                  />
                  <button
                    onClick={downloadQR}
                    className="text-sm bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition"
                  >
                    QR-Code herunterladen
                  </button>
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate(`/anlage/${id || 'neu'}`)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition"
                >
                  Zur Anlagenübersicht
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kunde (optional)
              </label>
              <select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="kunde-select"
              >
                <option value="">-- Kein Kunde zugeordnet --</option>
                {kunden.map((kunde) => (
                  <option key={kunde.id} value={kunde.id}>
                    {kunde.firma} ({kunde.kundennummer})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anlagentyp *
              </label>
              <select
                name="anlagentyp"
                value={formData.anlagentyp}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="anlagentyp-select"
              >
                <option value="Kälteanlage">Kälteanlage</option>
                <option value="Klimaanlage">Klimaanlage</option>
                <option value="Klimakammer">Klimakammer</option>
                <option value="Lüftungsanlage">Lüftungsanlage</option>
                <option value="Wärmepumpe">Wärmepumpe</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hersteller *
                </label>
                <input
                  type="text"
                  name="hersteller"
                  value={formData.hersteller}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. Daikin"
                  data-testid="hersteller-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modell *
                </label>
                <input
                  type="text"
                  name="modell"
                  value={formData.modell}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. VRV IV-S"
                  data-testid="modell-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Seriennummer *
                </label>
                <input
                  type="text"
                  name="seriennummer"
                  value={formData.seriennummer}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. SN123456789"
                  data-testid="seriennummer-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Leistung *
                </label>
                <input
                  type="text"
                  name="leistung"
                  value={formData.leistung}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. 12 kW"
                  data-testid="leistung-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Baujahr *
                </label>
                <input
                  type="text"
                  name="baujahr"
                  value={formData.baujahr}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. 2020"
                  data-testid="baujahr-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kältemittel
                </label>
                <input
                  type="text"
                  name="kaeltemittel"
                  value={formData.kaeltemittel}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. R32"
                  data-testid="kaeltemittel-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Standort - Adresse *
              </label>
              <input
                type="text"
                name="standort_adresse"
                value={formData.standort_adresse}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="z.B. Musterstraße 123, 12345 Musterstadt"
                data-testid="adresse-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Standort - Gebäude *
                </label>
                <input
                  type="text"
                  name="standort_gebaeude"
                  value={formData.standort_gebaeude}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. Gebäude A"
                  data-testid="gebaeude-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Standort - Raum *
                </label>
                <input
                  type="text"
                  name="standort_raum"
                  value={formData.standort_raum}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. Maschinenraum 1"
                  data-testid="raum-input"
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={loading || (qrCode && !isEdit)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition disabled:opacity-50"
                data-testid="submit-button"
              >
                <Save className="w-5 h-5" />
                {loading ? 'Speichern...' : (isEdit ? 'Aktualisieren' : 'Anlage erstellen')}
              </button>
              
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AnlageForm;